import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import { ExternalLink, Eye, CheckCircle2, XCircle, Clock, Calendar, Edit2, X, Save, Mail, AlertCircle } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { WatchlistEmailLog } from "@shared/schema";

const AUTHORITY_TYPES = [
  { code: "BHO", label: "BHO - Broker, arranging for transportation of household goods" },
  { code: "BPH", label: "BPH - Broker in arranging for transportation of freight (including household goods)" },
  { code: "BPO", label: "BPO - Broker, arranging for transportation of freight (except household goods)" },
  { code: "CHO", label: "CHO - Common carrier of household goods" },
  { code: "CMH", label: "CMH - Common carrier of property, including household goods" },
  { code: "CMO", label: "CMO - Common carrier of property (except household goods)" },
  { code: "CPA", label: "CPA - Common carrier of passengers by motor vehicle" },
  { code: "CPC", label: "CPC - Common carrier of passengers, in charter and special operations" },
  { code: "CPN", label: "CPN - Common carrier of passengers (nonrecipient of government)" },
  { code: "CPR", label: "CPR - Common carrier of passengers" },
  { code: "CX2", label: "CX2 - Certificate of Registration" },
  { code: "ENO", label: "ENO - US based enterprise owned/controlled by persons of Mexico" },
  { code: "NC/A", label: "NC/A - Name change / Reentitlement, active authority" },
  { code: "NC/I", label: "NC/I - Name change / Reentitlement, inactive authority" },
  { code: "PFB", label: "PFB - Freight forwarder of property, including household goods" },
  { code: "PFO", label: "PFO - Freight forwarder of household goods" },
  { code: "PFP", label: "PFP - Freight forwarder of property (except household goods)" },
  { code: "PHO", label: "PHO - Contract carrier of household goods" },
  { code: "PMH", label: "PMH - Contract carrier of property, including household goods" },
  { code: "PMO", label: "PMO - Contract carrier of property (except household goods)" },
  { code: "PPO", label: "PPO - Contract carrier of passengers" },
  { code: "REI", label: "REI - Reinstatement of authority" },
];

interface WatchlistDetailModalProps {
  watchlist: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WatchlistDetailModal({ watchlist, open, onOpenChange }: WatchlistDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: emailLogs = [], isLoading: emailLogsLoading } = useQuery<WatchlistEmailLog[]>({
    queryKey: ["/api/watchlists", watchlist?.id, "email-logs"],
    enabled: !!watchlist?.id && open,
    staleTime: 30000,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "matched":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "completed":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "expired":
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
      case "canceled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "active":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  const getEmailLogStatusColor = (status: string) => {
    switch (status) {
      case "sent":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "blocked_test":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleString();
  };

  const formatDocketNumber = () => {
    if (!watchlist.docketNumber) return "Not set";
    const prefix = watchlist.docketPrefix || "";
    const number = watchlist.docketNumber.toString().padStart(6, '0');
    const suffix = watchlist.docketSuffix || "";
    return `${prefix}${number}${suffix}`;
  };

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("PATCH", `/api/watchlists/${watchlist.id}`, data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/watchlists"] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      if (watchlist.workOrderId) {
        queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${watchlist.workOrderId}/watchlists`] });
      }
      toast({
        title: "Watchlist updated",
        description: "The watchlist has been updated successfully.",
      });
      setIsEditing(false);
      setEditedData({});
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Failed to update watchlist",
        description: error.message || "An error occurred while updating the watchlist.",
      });
    },
  });

  const handleEdit = () => {
    setEditedData({
      targetLabel: watchlist.targetLabel || "",
      letterType: watchlist.letterType || "",
      authorityTypeCode: watchlist.authorityTypeCode || "",
      authorityTypes: watchlist.authorityTypes?.join(", ") || "",
      watchlistDays: watchlist.watchlistDays || 90,
      watchlistType: watchlist.watchlistType || "multiple",
      status: watchlist.status || "active",
      externalReferenceId: watchlist.externalReferenceId || "",
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({});
  };

  const handleSave = () => {
    const dataToSend: any = {
      targetLabel: editedData.targetLabel || null,
      letterType: editedData.letterType || null,
      authorityTypeCode: editedData.authorityTypeCode || null,
      authorityTypes: editedData.authorityTypes
        ? editedData.authorityTypes.split(",").map((t: string) => t.trim()).filter(Boolean)
        : null,
      watchlistDays: parseInt(editedData.watchlistDays) || 90,
      watchlistType: editedData.watchlistType,
      status: editedData.status,
      externalReferenceId: editedData.externalReferenceId || null,
    };

    updateMutation.mutate(dataToSend);
  };

  const currentData = isEditing ? editedData : watchlist;

  // Don't render content if watchlist is null
  if (!watchlist) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>No Watchlist Selected</DialogTitle>
          </DialogHeader>
          <p>Please select a watchlist to view its details.</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Eye className={`w-6 h-6 ${
              watchlist.status === 'matched' ? 'text-green-500' :
              watchlist.status === 'completed' ? 'text-blue-500' :
              watchlist.status === 'expired' ? 'text-gray-400' :
              watchlist.status === 'canceled' ? 'text-red-500' :
              'text-amber-500'
            }`} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span>Watchlist Details</span>
                <Badge className={getStatusColor(watchlist.status)}>
                  {watchlist.status.charAt(0).toUpperCase() + watchlist.status.slice(1)}
                </Badge>
              </div>
            </div>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEdit}
                data-testid="button-edit-watchlist"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
            )}
            {isEditing && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                  data-testid="button-cancel-edit"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  data-testid="button-save-watchlist"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {updateMutation.isPending ? "Saving..." : "Save"}
                </Button>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Target Information */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">Target Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Target Type</p>
                <p className="font-medium">{watchlist.targetType?.toUpperCase() || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Target Identifier</p>
                <p className="font-medium text-blue-600 dark:text-blue-400">{watchlist.targetIdentifier}</p>
              </div>
              {watchlist.carrier && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Carrier</p>
                  {isEditing ? (
                    <p className="font-medium">{watchlist.carrier.name}</p>
                  ) : (
                    <Link
                      href={`/carriers/${watchlist.carrier.id}`}
                      className="font-medium text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                    >
                      {watchlist.carrier.name}
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              )}
              <div className="col-span-2">
                <Label htmlFor="targetLabel">Target Label</Label>
                {isEditing ? (
                  <Input
                    id="targetLabel"
                    value={editedData.targetLabel}
                    onChange={(e) => setEditedData({ ...editedData, targetLabel: e.target.value })}
                    placeholder="Enter target label"
                    data-testid="input-target-label"
                  />
                ) : (
                  <p className="font-medium">{watchlist.targetLabel || "Not set"}</p>
                )}
              </div>
            </div>
          </div>

          {/* DOT/Docket Information (Read-only) */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">DOT/Docket Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">USDOT Number</p>
                <p className="font-medium">{watchlist.usdotNumber || "Not set"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Docket Number</p>
                <p className="font-medium">{formatDocketNumber()}</p>
              </div>
            </div>
          </div>

          {/* Watchlist Configuration */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">Watchlist Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="watchlistType">Watchlist Type</Label>
                {isEditing ? (
                  <Select
                    value={editedData.watchlistType}
                    onValueChange={(value) => setEditedData({ ...editedData, watchlistType: value })}
                  >
                    <SelectTrigger data-testid="select-watchlist-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Match</SelectItem>
                      <SelectItem value="multiple">Continuous</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium">
                    {watchlist.watchlistType === 'single' ? 'Single Match' : 'Continuous'}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="watchlistDays">Duration (days)</Label>
                {isEditing ? (
                  <Input
                    id="watchlistDays"
                    type="number"
                    value={editedData.watchlistDays}
                    onChange={(e) => setEditedData({ ...editedData, watchlistDays: e.target.value })}
                    data-testid="input-watchlist-days"
                  />
                ) : (
                  <p className="font-medium">{watchlist.watchlistDays || "Not set"} days</p>
                )}
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                {isEditing ? (
                  <Select
                    value={editedData.status}
                    onValueChange={(value) => setEditedData({ ...editedData, status: value })}
                  >
                    <SelectTrigger data-testid="select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="matched">Matched</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium capitalize">{watchlist.status}</p>
                )}
              </div>
            </div>
          </div>

          {/* Filter Parameters */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">Filter Parameters</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="letterType">Letter Type</Label>
                {isEditing ? (
                  <Select
                    value={editedData.letterType || "none"}
                    onValueChange={(value) => setEditedData({ ...editedData, letterType: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="select-letter-type">
                      <SelectValue placeholder="Select letter type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Any (No Filter)</SelectItem>
                      <SelectItem value="Authority Grant">Authority Grant</SelectItem>
                      <SelectItem value="Mexican Carrier">Mexican Carrier</SelectItem>
                      <SelectItem value="Name Change">Name Change</SelectItem>
                      <SelectItem value="Reinstatement">Reinstatement</SelectItem>
                      <SelectItem value="Transfer">Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium">{watchlist.letterType || "Not set"}</p>
                )}
              </div>
              <div>
                <Label htmlFor="authorityTypeCode">Authority Type Code</Label>
                {isEditing ? (
                  <Select
                    value={editedData.authorityTypeCode || "none"}
                    onValueChange={(value) => setEditedData({ ...editedData, authorityTypeCode: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="select-authority-type-code">
                      <SelectValue placeholder="Select authority type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (No Filter)</SelectItem>
                      {AUTHORITY_TYPES.map((type) => (
                        <SelectItem key={type.code} value={type.code}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium">{watchlist.authorityTypeCode || "Not set"}</p>
                )}
              </div>
              <div className="col-span-2">
                <Label htmlFor="authorityTypes">Authority Types (comma-separated)</Label>
                {isEditing ? (
                  <Input
                    id="authorityTypes"
                    value={editedData.authorityTypes}
                    onChange={(e) => setEditedData({ ...editedData, authorityTypes: e.target.value })}
                    placeholder="e.g., Common, Contract, Broker"
                    data-testid="input-authority-types"
                  />
                ) : (
                  watchlist.authorityTypes && watchlist.authorityTypes.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {watchlist.authorityTypes.map((type: string, index: number) => (
                        <Badge key={index} variant="outline">{type}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium">Not set</p>
                  )
                )}
              </div>
            </div>
          </div>

          {/* External Reference */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">External Reference</h3>
            <div>
              <Label htmlFor="externalReferenceId">External Reference ID</Label>
              <p className="font-medium">{watchlist.externalReferenceId || "Not set"}</p>
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">
                  External reference ID cannot be edited to maintain data integrity
                </p>
              )}
            </div>
          </div>

          {/* Match History & Statistics */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">Match History & Statistics</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Total Matches
                </p>
                <p className="font-medium text-lg">{watchlist.matchCount || 0}</p>
              </div>
              {watchlist.lastMatchedAt && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Last Matched
                  </p>
                  <p className="font-medium">{formatDate(watchlist.lastMatchedAt)}</p>
                </div>
              )}
              {watchlist.completedAt && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    Completed
                  </p>
                  <p className="font-medium">{formatDate(watchlist.completedAt)}</p>
                </div>
              )}
              {watchlist.canceledAt && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <XCircle className="w-4 h-4 text-red-500" />
                    Canceled
                  </p>
                  <p className="font-medium">{formatDate(watchlist.canceledAt)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Work Order Link */}
          {watchlist.workOrder && (
            <div className="space-y-3">
              <h3 className="font-semibold text-lg border-b pb-2">Related Work Order</h3>
              <Link 
                href={`/work-orders/${watchlist.workOrder.id}`}
                className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Work Order #{watchlist.workOrder.id}
                {watchlist.workOrder.serviceType && ` - ${watchlist.workOrder.serviceType}`}
              </Link>
            </div>
          )}

          {/* System Information */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">System Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Created
                </p>
                <p className="font-medium">{formatDate(watchlist.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Last Updated
                </p>
                <p className="font-medium">{formatDate(watchlist.updatedAt)}</p>
              </div>
              {watchlist.helixWatchId && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Helix Watch ID</p>
                  <p className="font-medium text-xs">{watchlist.helixWatchId}</p>
                </div>
              )}
            </div>
          </div>

          {/* Watchlist Status Email Log — only shown in view mode */}
          {!isEditing && (
            <div className="space-y-3">
              <h3 className="font-semibold text-lg border-b pb-2 flex items-center gap-2">
                <Mail className="w-5 h-5 text-gray-500" />
                Watchlist Status Email Log
              </h3>
              {emailLogsLoading ? (
                <p className="text-sm text-gray-500">Loading email log...</p>
              ) : emailLogs.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No emails sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {emailLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-start gap-3 rounded-md border border-gray-200 dark:border-gray-700 p-3 text-sm"
                    >
                      <div className="mt-0.5">
                        {log.status === "sent" ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        ) : log.status === "failed" ? (
                          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                        ) : (
                          <Clock className="w-4 h-4 text-yellow-500 shrink-0" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium capitalize">{log.emailType}</span>
                          <Badge className={getEmailLogStatusColor(log.status)}>
                            {log.status === "blocked_test" ? "Test Mode" : log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                          </Badge>
                          <span className="text-gray-500 text-xs ml-auto shrink-0">
                            {formatDate(log.createdAt ? log.createdAt.toString() : null)}
                          </span>
                        </div>
                        {log.recipientEmails && log.recipientEmails.length > 0 && (
                          <p className="text-gray-500 text-xs mt-1 truncate">
                            To: {log.recipientEmails.join(", ")}
                          </p>
                        )}
                        {log.errorMessage && (
                          <p className="text-red-500 text-xs mt-1">{log.errorMessage}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
